import { Component, OnInit, NgZone, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { forkJoin, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';

import { SharedModule } from 'src/app/shared/shared.module';

import { JuresService } from '../../../../../../../data/commission/services/jures/jures.service';
import { CahiersService } from '../../../../../../../data/commission/services/cahiers/cahiers.service';
import { AdressesService } from '../../../../../../../data/declaration/services/adresses/adresses.service';
import { TranslateService } from '@ngx-translate/core';

import { ComJure } from '../../../../../../../data/commission/models/com-jure.model';
import { ComQualif } from '../../../../../../../data/commission/models/com-qualif.model';
import { Cahier } from '../../../../../../../data/habilitation/models/cahier.model';
import { Adresse } from '../../../../../../../data/declaration/models/adresse.model';
import { TypeAdresseEnum } from '../../../../../../../data/declaration/models/enums/type-adresse.enum';

import { AddEditQualificationComponent } from '../../modal/add-edit-qualification/add-edit-qualification.component';

@Component({
    selector: 'app-update-jure',
    templateUrl: './update-jure.component.html',
    styleUrls: ['./update-jure.component.scss'],
    standalone: true,
    imports: [SharedModule]
})
export class UpdateJureComponent implements OnInit {
    jure: ComJure;
    formUpdateJure: FormGroup;
    jureId: number;
    pageTitle: string;

    public qualifications: ComQualif[] = [];
    public qualificationsInactives: ComQualif[] = [];

    cahiersPossedes: Cahier[] = [];
    libelles: { [key: number]: string } = {};
    adressePostale: Adresse | null = null;

    public get nom() {
        return this.formUpdateJure.get('nom');
    }
    public get adresse() {
        return this.formUpdateJure.get('adresse');
    }
    public get codePostal() {
        return this.formUpdateJure.get('codePostal');
    }
    public get ville() {
        return this.formUpdateJure.get('ville');
    }

    constructor(
        private route: ActivatedRoute,
        private juresService: JuresService,
        private fb: FormBuilder,
        private cahierService: CahiersService,
        private readonly router: Router,
        private translate: TranslateService,
        private modalService: NgbModal,
        private adresseService: AdressesService,
        private zone: NgZone,
        private cdr: ChangeDetectorRef
    ) {
        this.formUpdateJure = this.fb.group({
            nom: ['', [Validators.required]],
            mail: ['', [Validators.email]],
            telFixe: [''],
            telPortable: [''],
            adresse: ['', [Validators.required]],
            complement: [''],
            codePostal: ['', [Validators.required]],
            ville: ['', [Validators.required]]
        });
    }

    private shouldOpenQualificationModal = false;

    ngOnInit() {
        this.jureId = +this.route.snapshot.params.id;

        this.shouldOpenQualificationModal = this.route.snapshot.queryParams && this.route.snapshot.queryParams.openQualification === '1';

        if (this.jureId) {
            this.loadJure();
        } else {
            this.translate.get('page.admin.jure.add.title').subscribe((res: string) => {
                this.pageTitle = res;
            });
            this.initForm();
        }
    }

    private loadJure(): void {
        forkJoin([this.juresService.getJureById(this.jureId), this.adresseService.getAdressesJure(this.jureId)]).subscribe(
            ([jure, adresses]) => {
                this.jure = jure;

                this.translate.get('page.admin.jure.update.title', { nom: jure.nom }).subscribe((res: string) => {
                    this.pageTitle = res;
                });

                this.adressePostale = adresses ? adresses.find(a => a.idRefAdresseType === TypeAdresseEnum.POSTALE) : null;
                this.initForm(jure, this.adressePostale);

                const today = moment();
                const all = jure.qualifications || [];

                const inactives = all.filter((q: any) => {
                    if (!q.dateFin) return false;
                    const df = moment(q.dateFin);
                    return df.isValid() && df.isBefore(today, 'day');
                });

                const actives = all.filter((q: any) => {
                    if (!q.dateFin) return true;
                    const df = moment(q.dateFin);
                    return !df.isValid() || df.isSameOrAfter(today, 'day');
                });

                this.qualificationsInactives = inactives;
                this.qualifications = actives;

                const uniqueCahierIds = Array.from(new Set(all.map(q => q.idCahier).filter(Boolean)));

                if (uniqueCahierIds.length === 0) {
                    this.cahiersPossedes = [];
                    this.libelles = {};
                } else {
                    forkJoin(uniqueCahierIds.map(id => this.cahierService.getCahierById(id).pipe(catchError(() => of(null))))).subscribe(
                        (cahiers: (Cahier | null)[]) => {
                            this.cahiersPossedes = cahiers.filter(Boolean) as Cahier[];
                            this.libelles = {};
                            this.cahiersPossedes.forEach(c => {
                                this.libelles[(c as any).id] = c.libelle || '';
                            });

                            if (this.shouldOpenQualificationModal) {
                                this.shouldOpenQualificationModal = false;
                                this.addQualification();
                            }
                        }
                    );
                    return;
                }

                if (this.shouldOpenQualificationModal) {
                    this.shouldOpenQualificationModal = false;
                    this.addQualification();
                }
            },
            error => {
                console.error('Erreur lors du chargement du juré', error);
            }
        );
    }

    initForm(jure?: ComJure, adresse?: Adresse) {
        this.formUpdateJure.patchValue({
            nom: jure ? jure.nom : '',
            telFixe: jure ? jure.telFixe || '' : '',
            mail: jure ? jure.mail : '',
            telPortable: jure ? jure.telPortable || '' : '',
            adresse: adresse ? adresse.adresse : '',
            complement: adresse ? adresse.complement : '',
            codePostal: adresse ? adresse.codePostal : '',
            ville: adresse ? adresse.ville : ''
        });
    }

    public getCollegesLibelle(qualification: ComQualif): string {
        return qualification.colleges ? qualification.colleges.map(college => college.libelle).join(', ') : '';
    }

    public getRaisonSocialeOperateurs(qualification: ComQualif): string {
        return qualification.operateurs ? qualification.operateurs.map(operateur => operateur.raisonSociale).join(', ') : '';
    }

    onClose() {
        this.router.navigate(['.'], { relativeTo: this.route.parent });
    }

    modifierJure() {
        if (!this.formUpdateJure.valid) return;

        const jureData = new ComJure({
            nom: this.formUpdateJure.value.nom,
            mail: this.formUpdateJure.value.mail,
            telFixe: this.formUpdateJure.value.telFixe,
            telPortable: this.formUpdateJure.value.telPortable
        });

        const adresseData = new Adresse({
            adresse: this.formUpdateJure.value.adresse,
            complement: this.formUpdateJure.value.complement,
            codePostal: this.formUpdateJure.value.codePostal,
            ville: this.formUpdateJure.value.ville
        });

        if (this.jureId) {
            this.juresService.updateJureById(this.jureId, jureData).subscribe(
                () => console.log('Jure updated successfully'),
                error => console.error('Error updating jure', error)
            );

            const adresse$ = this.adressePostale
                ? this.adresseService.updateAdresseJure(this.jureId, adresseData)
                : this.adresseService.createAdresseJure(this.jureId, adresseData);

            adresse$.subscribe(
                () => console.log(this.adressePostale ? 'Adresse updated successfully' : 'Adresse created successfully'),
                error => console.error(`Error saving adresse for idJure ${this.jureId}`, error)
            );

            // Optionnel: refresh UI
            this.loadJure();
            return;
        }

        this.juresService
            .addJure(jureData)
            .pipe(
                switchMap(jureId => {
                    return this.adresseService.createAdresseJure(jureId, adresseData).pipe(map(() => jureId));
                })
            )
            .subscribe(
                jureId => {
                    this.jureId = jureId;

                    this.router.navigate(['/main/admin/jures/jure/updateJure', jureId], { queryParams: { openQualification: '1' } });
                },
                error => console.error('Error adding juré', error)
            );
    }

    edit(qualification: ComQualif) {
        const modal = this.modalService.open(AddEditQualificationComponent, { backdrop: 'static' });
        modal.componentInstance.idJure = this.jureId;
        modal.componentInstance.idQualif = qualification.id;
        modal.componentInstance.comQualifAUpdate = qualification;
        modal.componentInstance.operateurSearch = qualification.operateurs && qualification.operateurs.length > 0 ? qualification.operateurs[0] : null;

        modal.result.then(
            result => {
                if (result === 'qualification_updated') {
                    this.loadJure();
                }
            },
            error => {
                throw error;
            }
        );
    }

    delete(_jureId: number, qualification: ComQualif) {
        const jureId = this.jureId;

        this.juresService.deleteComQualif(jureId, qualification).subscribe(
            () => {
                console.log('qualification rendue inactive');
                // ✅ plus de reload
                this.loadJure();
            },
            error => console.error('Error : la qualification est toujours active', error)
        );
    }

    addQualification() {
        const modal = this.modalService.open(AddEditQualificationComponent, { backdrop: 'static' });
        modal.componentInstance.idJure = this.jureId;
        modal.componentInstance.cahiersPossedes = this.cahiersPossedes;

        modal.result.then(
            result => {
                if (result.action === 'qualification_added') {
                    this.reloadQualificationsOnly();
                }
            },
            error => {
                throw error;
            }
        );
    }

    private reloadQualificationsOnly(): void {
        this.juresService.getJureById(this.jureId).subscribe(
            jure => {
                this.jure = jure;

                const all = jure.qualifications || [];
                const today = moment();

                this.qualifications = all.filter((q: any) => {
                    if (!q.dateFin) return true;
                    const df = moment(q.dateFin);
                    return !df.isValid() || df.isSameOrAfter(today, 'day');
                });

                this.qualificationsInactives = all.filter((q: any) => {
                    if (!q.dateFin) return false;
                    const df = moment(q.dateFin);
                    return df.isValid() && df.isBefore(today, 'day');
                });

                const uniqueCahierIds = Array.from(new Set(all.map(q => q.idCahier).filter(id => !!id)));

                if (uniqueCahierIds.length === 0) {
                    this.cahiersPossedes = [];
                    this.libelles = {};
                    return;
                }

                forkJoin(uniqueCahierIds.map(id => this.cahierService.getCahierById(id).pipe(catchError(() => of(null))))).subscribe(
                    (cahiers: (Cahier | null)[]) => {
                        this.cahiersPossedes = cahiers.filter(c => !!c) as Cahier[];
                        this.libelles = {};

                        this.cahiersPossedes.forEach(c => {
                            this.libelles[c.id] = c.libelle || '';
                        });

                        console.log('libelles', this.libelles);
                    }
                );
            },
            error => console.error('Erreur lors du rechargement des qualifications', error)
        );
    }

    reactivate(qualificationInactive: ComQualif) {
        const jureId = qualificationInactive.idJure;

        this.juresService.reactivateComQualif(jureId, qualificationInactive.id, qualificationInactive).subscribe(
            () => {
                console.log('qualification réactivée');
                // ✅ plus de reload
                this.loadJure();
            },
            error => console.error('Error : la qualification est toujours inactive', error)
        );
    }
}
